import { App } from '@capacitor/app';
import { CapacitorHttp } from '@capacitor/core';
import {
  checkIsMobileVersionCompatible,
  checkIsThereANewerMobileVersionAvailable
} from '@shared/helpers/check-mobile-version-compatibility.helpers';
import { isCapacitorNative } from '@utils/capacitor-utils';
import { useCallback } from 'react';
import packageJson from '../../../package.json';
import { useDispatch } from 'react-redux';
import { AppActions } from '@shared/store/app/app';
import { AppInfo } from '@shared/models/AppInfo.model';

// matches the structure of public/app-config.json
interface AppConfigResponse {
  minimumRequiredVersion: string;
  storeUrls: { ios: string; android: string };
  latestStoreVersions: { ios: string; android: string };
}

export const useAppInfo = () => {
  const dispatch = useDispatch();

  const fetchAppInfo = useCallback(async () => {
    const configFilePath = `${import.meta.env.VITE_APP_PUBLIC_URL}/app-config.json`;

    try {
      const timestamp = new Date().getTime(); // Cache-Busting Query Parameter
      const response = await CapacitorHttp.get({
        url: `${configFilePath}?t=${timestamp}`,
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0'
        }
      });

      if (response.status !== 200) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const config: AppConfigResponse = response.data;

      const appInfo: AppInfo = {
        version: packageJson.version,
        mode: import.meta.env.MODE || 'development',
        buildDate: import.meta.env.BUILD_DATE || null,
        isDevelopment: (import.meta.env.MODE || 'development') === 'development',
        isProduction: (import.meta.env.MODE || 'development') === 'production',
        storeUrls: {
          ios: 'https://apps.apple.com/gr/app/myefka/id6749564966',
          android: 'https://play.google.com/store/apps/details?id=efka.dashboard'
        },
        mobileVersion: undefined,
        isMobileVersionCompatible: true,
        isThereANewerMobileVersionAvailable: undefined
      };

      if (isCapacitorNative()) {
        const info = await App.getInfo();

        appInfo.mobileVersion = info.version;

        appInfo.isMobileVersionCompatible = checkIsMobileVersionCompatible(info.version, config.minimumRequiredVersion);
        appInfo.isThereANewerMobileVersionAvailable = checkIsThereANewerMobileVersionAvailable(
          info.version,
          config.latestStoreVersions
        );
        appInfo.storeUrls = config.storeUrls;
      }

      dispatch(AppActions.setAppInfo(appInfo));
    } catch (error) {
      console.error('Failed to fetch app config:', error);
    }
  }, [dispatch]);

  return {
    fetchAppInfo
  };
};
